// Error types
export const ERROR_TYPES = {
  NETWORK: 'network',
  AUTHENTICATION: 'authentication',
  VALIDATION: 'validation',
  PAYMENT: 'payment',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown'
}

// Custom Payment Error Class
export class PaymentError extends Error {
  constructor(message, code, type = ERROR_TYPES.UNKNOWN, details = {}) {
    super(message)
    this.name = 'PaymentError'
    this.code = code
    this.type = type
    this.details = details
    this.timestamp = new Date().toISOString()
  }
}

// Process error and normalize
export const processError = (error, context = {}) => {
  if (error instanceof PaymentError) {
    return error
  }

  // Network errors
  if (error.name === 'AbortError' || error.message.includes('aborted')) {
    return new PaymentError(
      'Tempo limite excedido. Por favor, tente novamente.',
      'timeout',
      ERROR_TYPES.TIMEOUT,
      { ...context, originalError: error.message }
    )
  }

  if (!navigator.onLine) {
    return new PaymentError(
      'Sem conexão com a internet. Verifique sua conexão.',
      'no_connection',
      ERROR_TYPES.NETWORK,
      { ...context, online: false }
    )
  }

  // API errors
  if (error.response) {
    const status = error.response.status
    const data = error.response.data

    if (status === 401 || status === 403) {
      return new PaymentError(
        'Erro de autenticação. Verifique as configurações.',
        'authentication_failed',
        ERROR_TYPES.AUTHENTICATION,
        { ...context, status, data }
      )
    }

    if (status === 422 || status === 400) {
      return new PaymentError(
        data?.message || 'Dados inválidos. Verifique as informações.',
        'validation_error',
        ERROR_TYPES.VALIDATION,
        { ...context, status, data }
      )
    }

    return new PaymentError(
      data?.message || `Erro no servidor (${status})`,
      `http_${status}`,
      ERROR_TYPES.PAYMENT,
      { ...context, status, data }
    )
  }

  // Generic error
  return new PaymentError(
    error.message || 'Erro desconhecido. Por favor, tente novamente.',
    'unknown_error',
    ERROR_TYPES.UNKNOWN,
    { ...context, originalError: error.message }
  )
}

// Log error (pode ser expandido para enviar para serviço de logging)
export const logError = (error) => {
  console.error('💥 Payment Error:', {
    message: error.message,
    code: error.code,
    type: error.type,
    details: error.details,
    timestamp: error.timestamp
  })

  // Salvar no localStorage para debug
  try {
    const errors = JSON.parse(localStorage.getItem('payment_errors') || '[]')
    errors.push({
      message: error.message,
      code: error.code,
      type: error.type,
      timestamp: error.timestamp
    })
    // Manter apenas os últimos 10 erros
    localStorage.setItem('payment_errors', JSON.stringify(errors.slice(-10)))
  } catch (e) {
    // Ignorar erros de localStorage
  }
}

// Retry helper
export const withRetry = async (fn, maxRetries = 3, delay = 1000) => {
  let lastError

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      // Não fazer retry em erros de validação ou autenticação
      if (error.type === ERROR_TYPES.VALIDATION || error.type === ERROR_TYPES.AUTHENTICATION) {
        throw error
      }

      if (attempt < maxRetries) {
        console.log(`🔄 Tentativa ${attempt}/${maxRetries} falhou. Tentando novamente em ${delay}ms...`)
        await new Promise(resolve => setTimeout(resolve, delay * attempt))
      }
    }
  }

  throw lastError
}
